import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Select,
    MenuItem,
    Skeleton,
    useMediaQuery,
    Stack,
    Paper,
    FormControl,
    InputLabel,
    Chip,
    Divider,
    TextField,
    InputAdornment,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import {
    Calendar,
    X,
    TrendingUp,
    TrendingDown,
    DollarSign,
    CalendarDays,
} from "lucide-react";
import { getEarningsCalendar, getEarnings } from "../../../api/stocks_api";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

/* ================= HELPERS ================= */
const sym = getCurrencySymbol();

const formatValue = (key, val) => {
    if (val === null || val === undefined) return "—";
    if (typeof val === "number") {
        if (key.includes("market_cap") || key.includes("revenue")) {
            if (val >= 1e12) return `${sym}${fmtL(val / 1e12, 2)}T`;
            if (val >= 1e9) return `${sym}${fmtL(val / 1e9, 2)}B`;
            if (val >= 1e6) return `${sym}${fmtL(val / 1e6, 2)}M`;
            if (val >= 1e3) return `${sym}${fmtL(val / 1e3, 2)}K`;
            return `${sym}${fmtL(val, 2)}`;
        }
        return fmtL(val, 2);
    }
    return val;
};

// Month number to abbreviation mapping
const monthNumToAbbr = {
    1: "jan", 2: "feb", 3: "mar", 4: "apr", 5: "may", 6: "jun",
    7: "jul", 8: "aug", 9: "sep", 10: "oct", 11: "nov", 12: "dec"
};

const EarningsCalendar = ({ variant = "dialog", onClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const isDialog = variant === "dialog";

    const [loading, setLoading] = useState(false);
    const [calendarData, setCalendarData] = useState([]);
    const [expandedDate, setExpandedDate] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState("monthly");
    const [currentWeekStart, setCurrentWeekStart] = useState(null);

    const [selectedMonth, setSelectedMonth] = useState(1);
    const [selectedYear, setSelectedYear] = useState(2026);

    const [tickerEarnings, setTickerEarnings] = useState(null);
    const [selectedTicker, setSelectedTicker] = useState(null);
    const [earningsOpen, setEarningsOpen] = useState(false);
    const [earningsData, setEarningsData] = useState({});
    const [earningsLoading, setEarningsLoading] = useState({});

    // const handleFetchEarnings = async (ticker) => {
    //     // ✅ If already fetched, don't call again
    //     if (earningsData[ticker]) return;

    //     setEarningsLoading(prev => ({
    //         ...prev,
    //         [ticker]: true
    //     }));

    //     const res = await getEarnings(ticker);

    //     if (res?.success) {
    //         setEarningsData(prev => ({
    //             ...prev,
    //             [ticker]: res.response_data
    //         }));
    //     }

    //     setEarningsLoading(prev => ({
    //         ...prev,
    //         [ticker]: false
    //     }));
    // };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const monthAbbr = monthNumToAbbr[selectedMonth];

                const res = await getEarningsCalendar(
                    monthAbbr,
                    selectedYear.toString(),
                    "sNFAIu2dFIRxj7tNM_coYy5PqrCIAKARu4meea7f7mVUAzFu0KbgDg=="
                );

                const daysArray = res?.data?.data?.days || res?.data?.days || [];

                if (res?.success && daysArray.length > 0) {
                    const formattedDays = daysArray.map(day => ({
                        date: day.date,
                        events: day.events.map(event => ({
                            ...event,
                            date: day.date,
                        }))
                    }));

                    setCalendarData(formattedDays);

                    // ✅ AUTO CALL earnings for first available ticker
                    const firstTicker =
                        formattedDays[0]?.events?.[0]?.ticker;

                    // if (firstTicker) {
                    //     await getEarnings(firstTicker); // fire silently
                    // }

                } else {
                    setCalendarData([]);
                }

            } catch (error) {
                console.error("Error fetching earnings:", error);
                setCalendarData([]);
            }

            setLoading(false);
        };

        fetchData();
    }, [selectedMonth, selectedYear]);

    const MONTHS = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ];

    const getYears = (start = 2020, endOffset = 2) => {
        const currentYear = new Date().getFullYear();
        return Array.from(
            { length: currentYear + endOffset - start + 1 },
            (_, i) => start + i
        );
    };

    useEffect(() => {
        if (viewMode === "weekly" && !currentWeekStart) {
            const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
            setCurrentWeekStart(firstDay);
        }
    }, [viewMode]);

    const getDaysInMonth = () => {
        return new Date(selectedYear, selectedMonth, 0).getDate();
    };

    const getFirstDayOfMonth = () => {
        return new Date(selectedYear, selectedMonth - 1, 1).getDay();
    };

    const handleDateClick = (date, events) => {
        if (events.length === 0) return;
        setExpandedDate(expandedDate === date ? null : date);
    };

    const handleViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
            setExpandedDate(null);
            if (newMode === "weekly" && !currentWeekStart) {
                const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
                setCurrentWeekStart(firstDay);
            }
        }
    };

    const navigateWeek = (direction) => {
        if (!currentWeekStart) return;

        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));

        if (newDate.getMonth() + 1 !== selectedMonth) {
            setSelectedMonth(newDate.getMonth() + 1);
            setSelectedYear(newDate.getFullYear());
        }

        setCurrentWeekStart(newDate);
        setExpandedDate(null);
    };

    const getWeekDates = () => {
        if (!currentWeekStart) return [];

        const dates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(date.getDate() + i);
            dates.push(date);
        }
        return dates;
    };

    const filterEventsBySearch = (events) => {
        if (!searchQuery.trim()) return events;

        const query = searchQuery.toLowerCase();
        return events.filter(event =>
            event.ticker.toLowerCase().includes(query) ||
            event.company_name.toLowerCase().includes(query)
        );
    };

    const getFilteredDayData = (dateStr) => {
        const dayData = calendarData.find(d => d.date === dateStr);
        if (!dayData) return null;

        const filteredEvents = filterEventsBySearch(dayData.events);
        return filteredEvents.length > 0 ? { ...dayData, events: filteredEvents } : null;
    };

    const renderDayCard = (day, dateStr, events, hasEvents, isExpanded, isToday) => {
        return (
            <div
                onClick={() => handleDateClick(dateStr, events)}
                style={{
                    backgroundColor: isToday
                        ? 'var(--color-success-light)'
                        : 'var(--color-surface)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: isToday
                        ? '2px solid var(--color-success)'
                        : isExpanded
                            ? '2px solid var(--color-success)'
                            : '1px solid var(--color-border-primary)',
                    borderRadius: '16px',
                    padding: isExpanded ? '20px' : '12px',
                    minHeight: isExpanded ? 'auto' : '120px',
                    cursor: hasEvents ? 'pointer' : 'default',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: !isExpanded && hasEvents ? 'scale(1)' : 'scale(1)',
                    boxShadow: isExpanded
                        ? 'var(--shadow-xl)'
                        : 'var(--shadow-sm)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                }}
                onMouseEnter={(e) => {
                    if (!isExpanded && hasEvents) {
                        e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isExpanded) {
                        e.currentTarget.style.borderColor = isToday ? 'var(--color-success)' : 'var(--color-border-primary)';
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    }
                }}
            >
                {/* Collapsed View */}
                {!isExpanded && (
                    <>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: isToday ? 'var(--color-success)' : 'var(--color-text-primary)',
                            marginBottom: '4px',
                        }}>
                            {day}
                        </div>

                        {loading ? (
                            <>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    sx={{ borderRadius: '6px', bgcolor: 'var(--color-border-primary)' }}
                                />
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    sx={{ borderRadius: '6px', bgcolor: 'var(--color-border-primary)' }}
                                />
                            </>
                        ) : hasEvents ? (
                            <>
                                {events.slice(0, 2).map((event, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            backgroundColor: searchQuery &&
                                                (event.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                    event.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
                                                ? 'var(--color-success-light)'
                                                : 'var(--color-surface-hover)',
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: "var(--font-weight-bold)",
                                            color: 'var(--color-text-primary)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            borderLeft: '3px solid var(--color-success)',
                                        }}
                                    >
                                        {event.ticker}
                                    </div>
                                ))}

                                {events.length > 2 && (
                                    <Tooltip
                                        title={
                                            <Box sx={{ p: 0.5 }}>
                                                {events.slice(2).map((event, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={`${event.ticker} - ${event.company_name}`}
                                                        // size="small"
                                                        sx={(theme) => ({
                                                            ...theme.typography.chipText,   // ✅ apply theme typography
                                                            m: 0.5,
                                                            bgcolor: 'var(--color-success-light)',
                                                            color: 'var(--color-text-primary)',
                                                            height: '24px',
                                                        })}
                                                    />
                                                ))}
                                            </Box>
                                        }
                                        arrow
                                        placement="auto"
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: "flip",
                                                    enabled: true,
                                                },
                                                {
                                                    name: "preventOverflow",
                                                    options: {
                                                        boundary: "viewport",
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <div style={{
                                            fontSize: 'var(--font-size-xs2)',
                                            fontWeight: "var(--font-weight-semibold)",
                                            color: 'var(--color-success)',
                                            marginTop: 'auto',
                                            cursor: 'help',
                                            padding: '4px 8px',
                                            backgroundColor: 'var(--color-success-light)',
                                            borderRadius: '6px',
                                            textAlign: 'center',
                                        }}>
                                            +{events.length - 2} more
                                        </div>
                                    </Tooltip>
                                )}
                            </>
                        ) : null}
                    </>
                )}

                {/* Expanded View */}
                {isExpanded && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography fontSize={24} fontWeight="var(--font-weight-bold)" color="var(--color-text-primary)">
                                    {new Date(dateStr).toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </Typography>
                                <Chip
                                    label={`${events.length} ${events.length === 1 ? 'Event' : 'Events'}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'var(--color-success-light)',
                                        color: 'var(--color-success)',
                                        fontWeight: "var(--font-weight-bold)",
                                        fontSize: "var(--font-size-sm)",
                                    }}
                                />
                            </Stack>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedDate(null);
                                }}
                                sx={{
                                    color: "var(--color-text-primary)",
                                    bgcolor: "var(--color-surface-hover)",
                                    "&:hover": {
                                        bgcolor: "var(--color-surface-active)",
                                        transform: "rotate(90deg)",
                                    },
                                    transition: "all 0.3s ease",
                                }}
                            >
                                <X size={18} />
                            </IconButton>
                        </Stack>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '16px',
                        }}>
                            {events.map((event, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        backgroundColor: 'var(--color-bg-secondary)',
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid var(--color-border-primary)',
                                        borderRadius: '12px',
                                        p: 2.5,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: 'var(--color-success)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'var(--shadow-lg)',
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/stocks/${event.ticker}/details/summary`);
                                    }}
                                >
                                    {/* Header */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="start" mb={2}>
                                        <Box>
                                            <Typography fontSize={20} fontWeight="var(--font-weight-bold)" color="var(--color-text-primary)" mb={0.5}>
                                                {event.ticker}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-xs2)" fontWeight={500} color="var(--color-text-muted)" lineHeight={1.4}>
                                                {event.company_name}
                                            </Typography>
                                        </Box>
                                        {event.reporting_time && (
                                            <Chip
                                                label={event.reporting_time}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'var(--color-success-light)',
                                                    color: 'var(--color-success)',
                                                    fontWeight: 600,
                                                    fontSize: '10px',
                                                }}
                                            />
                                        )}
                                    </Stack>

                                    <Divider sx={{ borderColor: 'var(--color-border-primary)', mb: 2 }} />

                                    {/* Market Cap */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)"
                                            fontWeight="var(--font-weight-semibold)" textTransform="uppercase" letterSpacing="0.5px">
                                            Market Cap
                                        </Typography>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-primary)" fontWeight="var(--font-weight-bold)">
                                            {formatValue('market_cap', event.market_cap)}
                                        </Typography>
                                    </Stack>

                                    {/* EPS Section */}
                                    <Box mb={1.5}>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)" letterSpacing="0.5px" mb={1}>
                                            Earnings Per Share
                                        </Typography>
                                        <Stack direction="row" gap={2}>
                                            <Box flex={1}>
                                                <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                                                    <TrendingUp size={15} color="var(--color-success)" />
                                                    <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)">
                                                        Forecast
                                                    </Typography>
                                                </Stack>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-success)" fontWeight="var(--font-weight-bold)">
                                                    {event.eps_forecast !== null ? `${sym}${event.eps_forecast}` : '—'}
                                                </Typography>
                                            </Box>
                                            <Box flex={1}>
                                                <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                                                    <TrendingDown size={15} color="var(--color-primary)" />
                                                    <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)">
                                                        Actual
                                                    </Typography>
                                                </Stack>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-primary)" fontWeight="var(--font-weight-bold)">
                                                    {event.eps_actual !== null ? `${sym}${event.eps_actual}` : '—'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    {/* Revenue Section */}
                                    <Box>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)" textTransform="uppercase" letterSpacing="0.5px" mb={1}>
                                            Revenue
                                        </Typography>
                                        <Stack direction="row" gap={2}>
                                            <Box flex={1}>
                                                <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                                                    <DollarSign size={15} color="var(--color-success)" />
                                                    <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)">
                                                        Forecast
                                                    </Typography>
                                                </Stack>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-success)" fontWeight="var(--font-weight-bold)">
                                                    {formatValue('revenue', event.revenue_forecast)}
                                                </Typography>
                                            </Box>
                                            <Box flex={1}>
                                                <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                                                    <DollarSign size={15} color="var(--color-primary)" />
                                                    <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" fontWeight="var(--font-weight-semibold)">
                                                        Actual
                                                    </Typography>
                                                </Stack>
                                                <Typography fontSize="var(--font-size-sm)" color="var(--color-primary)" fontWeight="var(--font-weight-bold)">
                                                    {formatValue('revenue', event.revenue_actual)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    {/* <Box mt={2}>
                                        <Chip
                                            label="Check Earnings Data"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFetchEarnings(event.ticker);
                                            }}
                                            sx={(theme) => ({
                                                ...theme.typography.chipText,  
                                                bgcolor: 'var(--color-success-light)',
                                                color: 'var(--color-success)',
                                                // fontWeight: 600,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: 'var(--color-success-light)',
                                                    opacity: 0.8
                                                }
                                            })}
                                        />
                                    </Box> */}

                                    {earningsLoading[event.ticker] && (
                                        <Typography mt={2} fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                            Loading earnings data...
                                        </Typography>
                                    )}

                                    {earningsData[event.ticker] && (
                                        <Box mt={2}>
                                            <Stack spacing={2}>
                                                {earningsData[event.ticker].map((item, i) => (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            background: 'var(--color-bg-secondary)',
                                                            border: '1px solid var(--color-border-primary)',
                                                            borderRadius: 2,
                                                            p: 2
                                                        }}
                                                    >
                                                        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-bold)" color="var(--color-success)">
                                                            FY {item.fiscal_year} Q{item.fiscal_quarter}
                                                        </Typography>

                                                        <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)">
                                                            {item.report_date} • {item.report_time}
                                                        </Typography>

                                                        <Stack direction="row" spacing={3} mt={1}>
                                                            <Box>
                                                                <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)">
                                                                    EPS
                                                                </Typography>
                                                                <Typography color="var(--color-text-primary)">
                                                                    {item.eps_actual ?? "—"}
                                                                </Typography>
                                                            </Box>

                                                            <Box>
                                                                <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)">
                                                                    Revenue
                                                                </Typography>
                                                                <Typography color="var(--color-text-primary)">
                                                                    {formatValue("revenue", item.revenue_actual)}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                </Box>
                            ))}
                        </div>
                    </Box>
                )}
            </div>
        );
    };

    const renderWeeklyView = () => {
        const weekDates = getWeekDates();
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <>
                {/* Week Navigation */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <IconButton
                        onClick={() => navigateWeek("prev")}
                        sx={{
                            color: "var(--color-text-primary)",
                            bgcolor: "var(--color-surface-hover)",
                            "&:hover": { bgcolor: "var(--color-success-light)" },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-bold)" color="var(--color-text-primary)">
                        {weekDates[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>

                    <IconButton
                        onClick={() => navigateWeek("next")}
                        sx={{
                            color: "var(--color-text-primary)",
                            bgcolor: "var(--color-surface-hover)",
                            "&:hover": { bgcolor: "var(--color-success-light)" },
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Stack>

                {/* Week Days Header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '12px',
                    marginBottom: '12px',
                }}>
                    {weekDays.map(day => (
                        <div key={day} style={{
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: 'var(--color-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}>
                            {day}
                        </div>
                    ))}
                </div>

                {/* Week Days Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: expandedDate ? '1fr' : 'repeat(7, 1fr)',
                    gap: '12px',
                }}>
                    {weekDates.map((date, index) => {
                        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const dayData = getFilteredDayData(dateStr);
                        const events = dayData?.events || [];
                        const hasEvents = events.length > 0;
                        const isExpanded = expandedDate === dateStr;
                        const isToday = new Date().toDateString() === date.toDateString();

                        return (
                            <div
                                key={dateStr}
                                style={{
                                    gridColumn: isExpanded ? 'span 7' : 'span 1',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                {renderDayCard(date.getDate(), dateStr, events, hasEvents, isExpanded, isToday)}
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderMonthlyView = () => {
        const daysInMonth = getDaysInMonth();
        const firstDay = getFirstDayOfMonth();
        const days = [];
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} style={{ minHeight: '120px' }} />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayData = getFilteredDayData(dateStr);
            const events = dayData?.events || [];
            const hasEvents = events.length > 0;
            const isExpanded = expandedDate === dateStr;
            const isToday = new Date().toDateString() === new Date(dateStr).toDateString();

            days.push(
                <div
                    key={day}
                    style={{
                        gridColumn: isExpanded ? 'span 7' : 'span 1',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {renderDayCard(day, dateStr, events, hasEvents, isExpanded, isToday)}
                </div>
            );
        }

        return (
            <>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '12px',
                    marginBottom: '12px',
                }}>
                    {weekDays.map(day => (
                        <div key={day} style={{
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: 'var(--color-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}>
                            {day}
                        </div>
                    ))}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '12px',
                }}>
                    {days}
                </div>
            </>
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // height: isDialog ? (isMobile ? "100vh" : "85vh") : "100%",
                height: "100%",
                borderRadius: isDialog ? 0 : 3,
                overflow: "hidden",
                bgcolor: 'var(--color-bg-primary)',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: "var(--color-surface)",
                    backdropFilter: 'blur(16px)',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid var(--color-border-primary)',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Typography variant="title">
                            Earnings Calendar
                        </Typography>
                    </Stack>

                    {isDialog && (
                        <IconButton
                            size="small"
                            onClick={onClose}
                            sx={{
                                color: "var(--color-text-primary)",
                                bgcolor: "var(--color-surface-hover)",
                                "&:hover": {
                                    bgcolor: "var(--color-surface-active)",
                                    transform: "rotate(90deg)",
                                },
                                transition: "all 0.3s ease",
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </Box>

            {/* Filters */}
            <Box px={3} py={2.5} sx={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                <Stack direction="column" gap={2}>
                    {/* View Mode Toggle & Date Selectors */}
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems="stretch">
                        {/* View Mode Toggle */}
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-primary)',
                                borderRadius: 2,
                                p: 0.5,
                            }}
                        >
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                sx={{
                                    width: '100%',
                                    '& .MuiToggleButton-root': {
                                        color: 'var(--color-text-muted)',
                                        border: 'none',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        px: 2,
                                        py: 1,
                                        '&.Mui-selected': {
                                            bgcolor: 'var(--color-success-light)',
                                            color: 'var(--color-success)',
                                            '&:hover': {
                                                bgcolor: 'var(--color-success-light)',
                                                opacity: 0.8
                                            },
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="monthly">
                                    <Calendar size={16} style={{ marginRight: '8px' }} />
                                    Monthly
                                </ToggleButton>
                                <ToggleButton value="weekly">
                                    <CalendarDays size={16} style={{ marginRight: '8px' }} />
                                    Weekly
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Paper>

                        {/* Month Selector */}
                        <Paper
                            elevation={0}
                            sx={{
                                flex: 1,
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-primary)',
                                borderRadius: 2,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: 'var(--color-success)',
                                    boxShadow: "var(--shadow-md)",
                                },
                            }}
                        >
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    sx={{
                                        fontSize: "var(--font-size-base)",
                                        fontWeight: "var(--font-weight-semibold)",
                                        color: "var(--color-text-muted)",
                                        "&.Mui-focused": { color: "var(--color-success)" },
                                    }}
                                >
                                    Month
                                </InputLabel>
                                <Select
                                    value={selectedMonth}
                                    label="Month"
                                    onChange={(e) => {
                                        setSelectedMonth(e.target.value);
                                        setExpandedDate(null);
                                        if (viewMode === "weekly") {
                                            const newDate = new Date(selectedYear, e.target.value - 1, 1);
                                            setCurrentWeekStart(newDate);
                                        }
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: 'var(--color-text-primary)',
                                    }}
                                >
                                    {MONTHS.map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>

                        {/* Year Selector */}
                        <Paper
                            elevation={0}
                            sx={{
                                flex: 1,
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-primary)',
                                borderRadius: 2,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: 'var(--color-success)',
                                    boxShadow: "var(--shadow-md)",
                                },
                            }}
                        >
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    sx={{
                                        fontSize: "var(--font-size-base)",
                                        fontWeight: "var(--font-weight-semibold)",
                                        color: "var(--color-text-muted)",
                                        "&.Mui-focused": { color: "var(--color-success)" },
                                    }}
                                >
                                    Year
                                </InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="Year"
                                    onChange={(e) => {
                                        setSelectedYear(e.target.value);
                                        setExpandedDate(null);
                                        if (viewMode === "weekly") {
                                            const newDate = new Date(e.target.value, selectedMonth - 1, 1);
                                            setCurrentWeekStart(newDate);
                                        }
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: 'var(--color-text-primary)',
                                    }}
                                >
                                    {getYears().map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    </Stack>

                    {/* Search Bar */}
                    <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: 'var(--color-surface)',
                            border: searchQuery ? '1px solid var(--color-success)' : '1px solid var(--color-border-primary)',
                            borderRadius: 2,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                borderColor: 'var(--color-success)',
                                boxShadow: "var(--shadow-md)",
                            },
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by ticker or company name..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setExpandedDate(null);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: searchQuery ? 'var(--color-success)' : 'var(--color-text-muted)', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchQuery("")}
                                            sx={{ color: 'var(--color-text-muted)' }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": { border: "none" },
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: 'var(--color-text-primary)',
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    color: 'var(--color-text-muted)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Paper>
                </Stack>
            </Box>

            {/* Calendar */}
            <Box flex={1} overflow="auto" p={3}>
                {loading && calendarData.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                border: '3px solid var(--color-border-primary)',
                                borderTopColor: 'var(--color-success)',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                },
                            }}
                        />
                        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-muted)">
                            Loading earnings data...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {viewMode === "monthly" ? renderMonthlyView() : renderWeeklyView()}
                        {!loading && calendarData.length === 0 && (
                            <Box
                                sx={{
                                    mt: 4,
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: 'var(--color-surface)',
                                    borderRadius: '16px',
                                    border: '1px solid var(--color-border-primary)',
                                }}
                            >
                                <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-muted)">
                                    No earnings data available for this period
                                </Typography>
                            </Box>
                        )}
                        {!loading && searchQuery && calendarData.every(day => filterEventsBySearch(day.events).length === 0) && (
                            <Box
                                sx={{
                                    mt: 4,
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: 'var(--color-surface)',
                                    borderRadius: '16px',
                                    border: '1px solid var(--color-border-primary)',
                                }}
                            >
                                <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-muted)">
                                    No results found for "{searchQuery}"
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default EarningsCalendar;